import styled from "styled-components";
import Category from "./Category";
import SortableCategory from "./SortableCategory";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import mediaQuery from "@utils/mediaQuery";
import useGetBodySystems from "@hooks/useGetBodySystems";
import useBodySystemPreferences from "@hooks/useBodySystemPreferences";
import { PatientType, PracticeType } from "@context/antibioticContext";
import { BODY_SYSTEMS_BY_PATIENT_AND_PRACTICE } from "@constants/bodysystems";
import { OnboardingContext } from "@context/onboardingContext";
import { getInferredPreferences } from "@utils/onboardingPreferenceUtil";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

interface Props {
  onClick?: (flow: string) => void;
  onCategoriesAvailable: () => void;
  patient: PatientType;
  practice: PracticeType;
  onChangePreferences?: () => void;
}

// OPTIMIZED: Compact header for mobile
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 292px;

  ${mediaQuery("tablet")`
    max-width: 593px;
    margin-bottom: 24px;
    gap: 12px;
    align-items: center;
  `}

  ${mediaQuery("wide")`
    max-width: 999px;
  `}
`;

const LabelWithPreference = styled.div`
  font-size: 18px;
  font-weight: var(--font-weight-bold);
  line-height: 120%;
  padding-top: 8px;
  width: 100%;

  ${mediaQuery("tablet")`
    font-size: var(--font-size-3xl);
    padding-top: 12px;
    width: auto;
  `}
`;

const PreferenceText = styled.span`
  color: var(--interactive-primary);
  font-weight: var(--font-weight-semibold);
`;

const PreferencePill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: var(--bg-card);
  border: 2px solid var(--interactive-primary);
  border-radius: 8px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--interactive-primary);
`;

const PillButton = styled.button`
  border: none;
  background: none;
  color: var(--interactive-primary);
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-left: 4px;

  &:hover {
    color: #018786;
  }
`;

const Wrapper = styled.div`
  display: grid;
  gap: 12px;
  justify-content: center;
  grid-template-columns: repeat(2, 140px);

  ${mediaQuery("tablet")`
    grid-template-columns: repeat(3, 187px);
    gap: 16px;
    justify-content: flex-start;
  `}

  ${mediaQuery("wide")`
    grid-template-columns: repeat(5, 187px);
  `}
`;

const ScrollContainer = styled.div`
  position: relative;

  &::after {
    content: '';
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.9));
    pointer-events: none;

    ${mediaQuery("tablet")`
      display: none;
    `}
  }
`;

const CustomiseButton = styled.button`
  border: 2px solid var(--interactive-primary);
  background: transparent;
  color: var(--interactive-primary);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  padding: 6px 16px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--interactive-primary);
    color: white;
  }
`;

const EditToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f0f7f7;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  color: #555;
  max-width: 292px;

  ${mediaQuery("tablet")`
    max-width: 593px;
  `}

  ${mediaQuery("wide")`
    max-width: 999px;
  `}
`;

const ResetLink = styled.button`
  border: none;
  background: none;
  color: var(--interactive-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  white-space: nowrap;
  margin-left: 12px;

  &:hover {
    color: #018786;
  }
`;

const HiddenBadge = styled.button`
  border: none;
  background: none;
  color: #888;
  font-size: var(--font-size-sm);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: var(--interactive-primary);
  }
`;

const CategorySelection = ({
  onClick,
  onCategoriesAvailable,
  patient,
  practice,
  onChangePreferences,
}: Props) => {
  const { bodySystems, errorMessage } = useGetBodySystems();
  const { userData } = useContext(OnboardingContext) || {};
  const [selectedBodySystem, setSelectedBodySystem] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    reorder,
    toggleHidden,
    resetPreferences,
    isHidden,
    applyPreferences,
    hasCustomPreferences,
    hiddenCount,
  } = useBodySystemPreferences();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 5 },
  });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleOnClick = (name: string, flow: string) => {
    setSelectedBodySystem(name);
    onClick?.(flow);
  };

  useEffect(() => {
    if (bodySystems || errorMessage) {
      onCategoriesAvailable();
    }
  }, [bodySystems, errorMessage, onCategoriesAvailable]);

  // NEW: Get suggestions from onboarding (but don't auto-apply)
  const suggestedPreferences = useMemo(() => {
    if (!userData?.practiceArea) return null;
    return getInferredPreferences(userData);
  }, [userData?.practiceArea]);

  const filteredBodySystems = useMemo(
    () =>
      bodySystems?.filter(({ name }) =>
        BODY_SYSTEMS_BY_PATIENT_AND_PRACTICE[patient][practice].includes(name)
      ),
    [bodySystems, patient, practice]
  );

  // Apply user preferences on top of patient/practice filtering
  const displayedBodySystems = useMemo(
    () =>
      filteredBodySystems
        ? applyPreferences(filteredBodySystems, isEditMode)
        : undefined,
    [filteredBodySystems, applyPreferences, isEditMode]
  );

  const hasPreferences = localStorage.getItem('whata_remember_prefs') === 'true';

  const handleChangePreferences = () => {
    localStorage.removeItem('whata_remember_prefs');
    onChangePreferences?.();
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !displayedBodySystems) return;

      const names = displayedBodySystems.map((bs) => bs.name);
      const oldIndex = names.indexOf(active.id as string);
      const newIndex = names.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(names, oldIndex, newIndex);
      reorder(newOrder);
    },
    [displayedBodySystems, reorder]
  );

  const handleReset = () => {
    resetPreferences();
  };

  const patientLabel = patient === "Adult" ? "Adult" : "Child";
  const practiceLabel = practice === "Primary" ? "Community" :
                        practice === "Secondary" ? "Hospital" : "Surgical Prophylaxis";

  // Determine if icons should be shown based on practice type
  // Hide icons for Surgical Prophylaxis (any practice that's not Primary or Secondary)
  const showIcons = practice === "Primary" || practice === "Secondary";

  // Count hidden items within the current filtered set
  const currentHiddenCount = useMemo(() => {
    if (!filteredBodySystems) return 0;
    return filteredBodySystems.filter((bs) => isHidden(bs.name)).length;
  }, [filteredBodySystems, isHidden]);

  const sortableIds = useMemo(
    () => displayedBodySystems?.map((bs) => bs.name) ?? [],
    [displayedBodySystems]
  );

  return (
    <ScrollContainer>
      {/* Always show current selections in heading */}
      <Header>
        <LabelWithPreference>
          Select a category in{' '}
          <PreferenceText>
            {patientLabel} & {practiceLabel}
          </PreferenceText>
        </LabelWithPreference>

        {/* Only show pill badge when preferences are actually saved */}
        {hasPreferences && !isEditMode && (
          <PreferencePill>
            <span>&#10003;</span>
            <span>Saved selections</span>
            <PillButton onClick={handleChangePreferences}>
              Change
            </PillButton>
          </PreferencePill>
        )}

        <CustomiseButton onClick={() => setIsEditMode(!isEditMode)}>
          {isEditMode ? "Done" : "Customise"}
        </CustomiseButton>
      </Header>

      {isEditMode && (
        <EditToolbar>
          <span>Drag tiles to reorder. Click &minus;/+ to hide or show.</span>
          {hasCustomPreferences && (
            <ResetLink onClick={handleReset}>Reset to default</ResetLink>
          )}
        </EditToolbar>
      )}

      {!isEditMode && currentHiddenCount > 0 && (
        <HiddenBadge onClick={() => setIsEditMode(true)}>
          {currentHiddenCount} hidden &mdash; Edit
        </HiddenBadge>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
          <Wrapper>
            {errorMessage && <div>{errorMessage}</div>}
            {displayedBodySystems?.map(({ _id, icon, name, flow }) => (
              <SortableCategory
                key={_id}
                image={icon}
                name={name}
                flow={flow}
                selectedValue={selectedBodySystem}
                onClick={handleOnClick}
                showIcon={showIcons}
                isEditMode={isEditMode}
                isHidden={isHidden(name)}
                onToggleHidden={toggleHidden}
              />
            ))}
          </Wrapper>
        </SortableContext>
      </DndContext>
    </ScrollContainer>
  );
};

export default CategorySelection;